import React, { useId } from "react";
import Dialog from "@mui/material/Dialog";

import styles from "./DashboardConfirmationDialog.module.scss";

type Props = {
  open: boolean;
  kind: "archive" | "reminder";
  description: React.ReactNode;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

function ConfirmationIllustration({ kind, clipId }: { kind: Props["kind"]; clipId: string }) {
  return (
    <svg className={styles.illustration} viewBox="0 0 260 190" fill="none" aria-hidden="true">
      <circle cx="131" cy="93" r="77" fill="#FFE9D5" />
      <circle cx="213" cy="47" r="5" fill="#F6C59B" />
      <circle cx="38" cy="117" r="4" fill="#F6C59B" />
      <path d="m46 47 3-6 3 6 6 3-6 3-3 6-3-6-6-3 6-3Z" fill="#EE9B58" />
      <ellipse cx="132" cy="171" rx="79" ry="7" fill="#EBD7C7" />
      {kind === "archive" ? (
        <>
          <defs><clipPath id={clipId}><rect x="45" y="25" width="170" height="95" /></clipPath></defs>
          <g clipPath={`url(#${clipId})`}>
            <g className={styles.hidingFigure}>
              <path d="M96 116v-10c0-17 14-29 32-29s32 12 32 29v10" fill="#426584" />
              <rect x="118" y="76" width="20" height="18" rx="8" fill="#E5A77E" />
              <rect x="107" y="37" width="43" height="47" rx="20" fill="#F5C49F" />
              <path d="M106 60c-5-20 7-31 23-30 20 0 28 15 21 32l-6-14c-10 7-24 5-32 2l-6 10Z" fill="#253D59" />
              <circle cx="119" cy="62" r="2" fill="#253D59" />
              <circle cx="138" cy="62" r="2" fill="#253D59" />
              <path d="M123 73c3 3 7 3 10 0" stroke="#A96C4E" strokeWidth="2" strokeLinecap="round" />
              <path d="M100 100v16M157 100v16" stroke="#F5C49F" strokeWidth="12" strokeLinecap="round" />
            </g>
          </g>
          <rect x="72" y="112" width="116" height="54" rx="7" fill="#E58B42" />
          <path d="M72 120h116v12H72z" fill="#D9772B" opacity=".35" />
          <g className={styles.archiveLid}>
            <rect x="66" y="105" width="128" height="14" rx="5" fill="#F0A263" />
            <path d="M72 118h116" stroke="#C76B27" strokeWidth="2" />
          </g>
          <rect x="112" y="132" width="35" height="19" rx="4" fill="#FFF4E7" />
          <path d="M121 142h17" stroke="#C67B3E" strokeWidth="3" strokeLinecap="round" />
        </>
      ) : (
        <>
          <path d="m68 126-6 37M83 127l10 36" stroke="#253D59" strokeWidth="12" strokeLinecap="round" />
          <path d="M59 163h11v6H51c0-4 3-6 8-6ZM87 163h10l8 6H87v-6Z" fill="#20344C" />
          <path d="M52 128V99c0-13 10-23 23-23 13 0 23 10 23 23v29" fill="#E58B42" />
          <path d="m92 98 13-9" stroke="#F5C49F" strokeWidth="10" strokeLinecap="round" />
          <path d="m53 99-4 20" stroke="#F5C49F" strokeWidth="10" strokeLinecap="round" />
          <rect x="62" y="41" width="33" height="38" rx="16" fill="#F5C49F" />
          <path d="M61 54c-3-16 6-25 18-25 14 0 23 11 18 27l-7-8-29 6Z" fill="#253D59" />
          <path d="M59 43c0-13 9-20 21-20 13 0 20 8 20 20" fill="#E58B42" />
          <path d="M58 43h45c-4 7-23 9-45 0Z" fill="#C76B27" />
          <circle cx="84" cy="59" r="1.8" fill="#253D59" />
          <path d="m81 70 6-2" stroke="#A96C4E" strokeWidth="2" strokeLinecap="round" />
          <path d="M108 64c24-25 62-20 76-2" stroke="#DEB08C" strokeWidth="2" strokeDasharray="4 5" strokeLinecap="round" />
          <g className={styles.postedLetter}>
            <rect x="91" y="63" width="37" height="27" rx="4" fill="white" stroke="#DAA174" strokeWidth="1.5" />
            <path d="m94 67 15 11 16-11M94 87l10-10M125 87l-10-10" stroke="#E58B42" strokeWidth="1.5" strokeLinejoin="round" />
          </g>
          <path d="M171 135v34" stroke="#426584" strokeWidth="9" />
          <path d="M156 170h30" stroke="#253D59" strokeWidth="5" strokeLinecap="round" />
          <path d="M136 99a35 35 0 0 1 70 0v45h-70V99Z" fill="#426584" />
          <path d="M171 65c19 0 35 15 35 34v45h-14V99c0-16-9-29-21-34Z" fill="#314E6C" />
          <rect x="146" y="94" width="40" height="7" rx="3.5" fill="#20344C" />
          <rect x="155" y="114" width="23" height="15" rx="3" fill="#F6E9DD" />
          <path d="m158 118 8 5 9-5" stroke="#B87A48" strokeWidth="1.5" />
          <path className={styles.postSparkle} d="m210 80 3-6 3 6 6 3-6 3-3 6-3-6-6-3 6-3Z" fill="#E58B42" />
        </>
      )}
    </svg>
  );
}

export default function DashboardConfirmationDialog({
  open, kind, description, loading = false, onCancel, onConfirm,
}: Props) {
  const id = useId();
  const isArchive = kind === "archive";

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-description`}
      className={styles.dialogRoot}
      PaperProps={{ className: styles.dialogPaper }}
      maxWidth={false}
    >
      <div className={styles.artPanel}>
        <ConfirmationIllustration kind={kind} clipId={`${id.replace(/:/g, "")}-hide`} />
        <button type="button" className={styles.closeButton} onClick={onCancel} disabled={loading} aria-label="Close confirmation"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg></button>
      </div>
      <div className={styles.content}>
        
        <h2 id={`${id}-title`}>{isArchive ? "Archive consent bundle" : "Send reminder?"}</h2>
        <p id={`${id}-description`} className={styles.description}>{description}</p>
        <div className={styles.actions}>
          <button type="button" className={styles.cancelButton} onClick={onCancel} disabled={loading}>Cancel</button>
          <button type="button" className={styles.confirmButton} onClick={onConfirm} disabled={loading} aria-busy={loading}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{isArchive ? <><path d="M3 8v13h18V8M10 12h4" /><rect x="1" y="3" width="22" height="5" /></> : <><path d="m22 2-7 20-4-9-9-4L22 2ZM22 2 11 13" /></>}</svg>
            {loading ? (isArchive ? "Archiving..." : "Sending...") : (isArchive ? "Archive" : "Yes, Send")}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
